import React from 'react';
import {defineMessages} from 'react-intl';
import {Card} from '@igp/shared/components/card';
import {LocalizedText} from '@igp/shared/components/i18n';
import {CasinoCoinSvgIcon, UsdSvgIcon} from '@igp/shared/components/icons';
import {TextWithEmptyCheck} from '@igp/shared/components/label';
import {useUserProfileDetails} from '@igp/shared/features/user-profile';
import {RealtimeUpdatesMode} from '@igp/shared/redux/realtime';
import type {CustomTheme} from '@igp/shared/style';
import {Stack, Typography} from '@mui/material';
import {makeStyles} from 'tss-react/mui';

import {useAgentId} from '@features/account/hooks';

const localized = defineMessages({
    usdBalance: {
        id: 'CurrentBalance_usd',
        defaultMessage: 'USD',
    },
    casinoCoinBalance: {
        id: 'CurrentBalance_casino_coin',
        defaultMessage: 'Casino Coin',
    },
});

export const useStyles = makeStyles()((theme: CustomTheme) => ({
    currentBalanceCard: {
        minWidth: theme.custom.coinCardWidth,
        border: `1px solid ${theme.custom.palette.content.border}`,
    },
    currentBalanceCardTitle: {
        color: theme.palette.text.secondary,
    },
    currentBalanceCardContentWrapper: {
        display: 'flex',
        flexDirection: 'row',
        gap: theme.spacing(0.5),
        alignItems: 'center',
        color: theme.palette.text.primary,
    },
}));

export function CurrentBalance() {
    const uid = useAgentId();
    const {item} = useUserProfileDetails({
        id: uid,
        fields: ['balance', 'finance.casino_coin_balance'],
        viewType: 'CurrentBalance',
        realtimeMode: RealtimeUpdatesMode.Silent,
        isCancelable: false,
    });
    const {classes} = useStyles();
    return (
        <Stack direction="row" spacing={1}>
            <Card spacing="small" background="light-grey" className={classes.currentBalanceCard}>
                <Stack direction="column" spacing={0.25}>
                    <Typography variant="body2" className={classes.currentBalanceCardTitle}>
                        <LocalizedText label={localized.usdBalance} />
                    </Typography>
                    <div className={classes.currentBalanceCardContentWrapper}>
                        <UsdSvgIcon bgColor="#146FF9" symbolColor="white" />
                        <TextWithEmptyCheck value={item?.finance?.balance?.toString()} />
                    </div>
                </Stack>
            </Card>
            <Card spacing="small" background="light-grey" className={classes.currentBalanceCard}>
                <Stack direction="column" spacing={0.25}>
                    <Typography variant="body2" className={classes.currentBalanceCardTitle}>
                        <LocalizedText label={localized.casinoCoinBalance} />
                    </Typography>
                    <div className={classes.currentBalanceCardContentWrapper}>
                        <CasinoCoinSvgIcon bgColor="#900DCE" symbolColor="white" />
                        <TextWithEmptyCheck value={item?.finance?.casino_coin_balance?.toString()} />
                    </div>
                </Stack>
            </Card>
        </Stack>
    );
}
